import __i18nConfig from '@next-translate-root/i18n.js';
import __loadNamespaces from 'next-translate/loadNamespaces';
import { HeadMeta } from '@/components/Molecules/HeadMeta/HeadMeta';
import { ForumPage } from '@/components/Pages/Events/Forum/ForumPage';
import { WebinarPage } from '@/components/Pages/Events/Webinar/WebinarPage';
import { EventSessionTemplate } from '@/components/Templates/Events/EventSessionTemplate';
import { BaseAppContextContainer } from '@/contexts/AppContext/AppContextContainer';
import { getAppContextServerSideProps } from '@/contexts/AppContext/AppContextServer';
import type { IContentItem, IEvent, IEventSession, IEventWebinar, IPerson, IRole } from '@/interfaces';
import { ContentType } from '@/interfaces';
import type { IBaseAnalyticsData } from '@/services/Analytics/bnefAnalyticsInterfaces';
import { calculateLifeCycleState, LifeCycleState } from '@/utils/eventUtils';
import type { ShareAction } from 'bnef';
import type { KeyedAttachments } from '../../components/Pages/Insights/useInsightExecSumActions';
const getEventLifeCycleStateKey = (event: IEvent, session?: IEventSession): string => {
  switch (calculateLifeCycleState(event)) {
    case LifeCycleState.pre:
      return 'pre';
    case LifeCycleState.live:
      return 'live';
  }
  if (session?.videoResourceId) {
    return 'onDemand';
  }
  return 'post';
};
const getInitialAnalyticsMetadata = (event: IEvent, session?: IEventSession): IBaseAnalyticsData => ({
  page: {
    pageInfo: {
      pageName: `BNEF: Events: ${event.type}: ${session?.title || event.title}`
    },
    category: {
      siteSection: 'BNEF: Events',
      subSection1: `BNEF: Events: ${event.type}`
    },
    content: {
      type: event.type,
      id: `${event.type}-${getEventLifeCycleStateKey(event, session)}-${event.id}`
    }
  }
});
interface IProps {
  event: IEvent;
  attachments?: KeyedAttachments;
  session?: IEventSession;
  sessions?: {
    session: IEventSession;
    participants: IPerson[];
  }[];
  people?: IRole[];
  related?: IContentItem[];
  shareToken: null | ShareAction;
}
const getServerSideProps = getAppContextServerSideProps(async ({
  query,
  api
}) => {
  const data = await api.get({
    path: '/events/[ID]',
    replacements: {
      id: query.id.toString()
    }
  });
  if (data) {
    if (data.redirect) {
      return {
        redirect: {
          destination: data.redirect,
          statusCode: 302
        }
      };
    }
    if (data.event && (data.session || data.sessions)) {
      return {
        props: {
          analyticsMetadata: getInitialAnalyticsMetadata(data.event, data.session),
          event: data.event,
          session: data.session,
          attachments: data.attachments,
          sessions: data.sessions,
          people: data.people,
          related: data.related,
          shareToken: data.shareToken
        }
      };
    }
  }
  return {
    notFound: true
  };
}, async ({
  query,
  res,
  api
}) => {
  const data = await api.get({
    path: '/public/events/[ID]',
    replacements: {
      id: query.id as string
    }
  });
  if (data !== null) {
    const event = data?.event;
    return {
      type: event.type,
      title: event.title,
      publishDate: event.displayDate,
      description: event.summary,
      image: event.image,
      tags: event.tags
    };
  }
  res.statusCode = 404;
});
export default class extends BaseAppContextContainer<IProps> {
  public render() {
    const {
      session,
      event
    } = this.props;
    let page;
    if (event.type === ContentType.forum && this.props.sessions) {
      page = <ForumPage event={event} sessions={this.props.sessions} />;
    } else if (event.type === ContentType.webinar) {
      page = <WebinarPage event={event as IEventWebinar} attachments={this.props.attachments} session={this.props.session} people={this.props.people} related={this.props.related} shareToken={this.props.shareToken} />;
    } else {
      // ContentType.publicwebinar
      page = <EventSessionTemplate attachments={this.props.attachments} event={this.props.event} session={this.props.session} people={this.props.people} related={this.props.related} shareToken={this.props.shareToken} />;
    }
    return <>
                <HeadMeta title={session?.title || event.title} type={session?.videoResourceId ? ContentType.video : ContentType.event} description={session?.description ?? event?.description} image={session?.leadImage?.baseUrl ?? event.leadImage?.baseUrl} />
                {page}
            </>;
  }
}
async function __Next_Translate__getServerSideProps__1a1a25d1fc5__(ctx) {
  if (typeof global !== 'undefined') {
    global.i18nConfig = __i18nConfig;
  }
  const res = await getServerSideProps(ctx);
  return {
    ...res,
    props: {
      ...(res.props || {}),
      ...(await __loadNamespaces({
        ...ctx,
        ...__i18nConfig,
        pathname: '/events/[id]',
        loaderName: 'getServerSideProps',
        loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({}))
      }))
    }
  };
}
export { __Next_Translate__getServerSideProps__1a1a25d1fc5__ as getServerSideProps };